import type { AgentAccent, AudienceInput, AudienceView, MakerAgent, MakerAgentUpsert } from '@ralph/contracts'

/**
 * The builder's editing model.
 *
 * Deliberately identical in shape to `MakerAgentUpsert` minus its change note: the form
 * holds exactly what a maker may write and nothing else, so there is no mapping step where
 * a policy-plane field could be introduced by accident. The server's strict schema is the
 * enforcement (AGM-021); this is the client agreeing with it rather than testing it.
 *
 * `audience` is NOT here on purpose. Who can use it changes only through publication — that
 * is the moment the maker is shown what they are sharing and acknowledges it (AGM-042), and
 * where the review rule splits what goes live from what waits (PRD-26 D-40) — so the picker
 * edits a separate `AudienceInput` that the publish step submits.
 */
export type BuilderDraft = {
  displayName: string
  description: string
  tagline: string
  icon: string
  accent: AgentAccent
  category: string
  starters: string[]
  instructions: string
  grounded: boolean
  remembersPreferences: boolean
  knowledgeBaseIds: string[]
  capabilityIds: string[]
  copyPolicy: 'auto' | 'allowed' | 'blocked'
}

export const draftFromAgent = (agent: MakerAgent): BuilderDraft => ({
  displayName: agent.displayName,
  description: agent.description,
  tagline: agent.tagline,
  icon: agent.icon,
  accent: agent.accent,
  category: agent.category,
  starters: [...agent.starters],
  instructions: agent.instructions,
  grounded: agent.grounded,
  remembersPreferences: agent.remembersPreferences,
  knowledgeBaseIds: [...agent.knowledgeBaseIds],
  capabilityIds: [...agent.capabilityIds],
  copyPolicy: agent.copyPolicy
})

export const toUpsert = (draft: BuilderDraft, changeNote: string): MakerAgentUpsert => ({
  ...draft,
  starters: draft.starters.map((starter) => starter.trim()).filter((starter) => starter !== ''),
  changeNote: changeNote.trim()
})

/**
 * Structural comparison with sorted keys, so an edit typed and then undone stops counting
 * as unsaved. (Same reasoning as the admin studio's `stableJson`, kept local because the
 * two trees are edited by different people and a shared helper would couple them.)
 */
const stableJson = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entry]) => entry !== undefined)
      // Code points, not `localeCompare`: this is a canonical form used to decide whether the
      // draft is dirty, and a locale-sensitive collation would make that answer depend on the
      // language the reader happens to be in. Keys are identifiers, and identifiers never sort
      // by anybody's alphabet (PRD-27 D-17).
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${stableJson(entry)}`).join(',')}}`
  }
  return JSON.stringify(value) ?? 'null'
}

export const isDirty = (draft: BuilderDraft, agent: MakerAgent): boolean =>
  stableJson(draft) !== stableJson(draftFromAgent(agent))

/** The picker's value for what is live now: groups by id, never by name (PRD-26 GRP-102). */
export const audienceInputOf = (view: AudienceView): AudienceInput => ({
  kind: view.kind,
  groupIds: view.kind === 'groups' ? view.groups.map((group) => group.id) : []
})

/** Same kind, and the same groups in any order when that matters. */
export const audienceEquals = (a: AudienceInput, b: AudienceInput): boolean => {
  if (a.kind !== b.kind) return false
  if (a.kind !== 'groups') return true
  return stableJson([...new Set(a.groupIds)].sort()) === stableJson([...new Set(b.groupIds)].sort())
}

/** Whether the picker holds something the server can take: "Specific groups" needs a group. */
export const isCompleteAudience = (audience: AudienceInput): boolean =>
  audience.kind !== 'groups' || audience.groupIds.length > 0

/**
 * Derives the stored identifier from the name the maker typed.
 *
 * The identifier is a URL and a database key, never a label — no maker surface displays it,
 * and nobody is asked to invent one (AGM-020 forbids the technical field, not the column).
 * A collision is resolved by the caller retrying with `unique()`, because two people are
 * entitled to draft a "Travel Helper" without meeting each other's agent.
 */
export const suggestAgentId = (displayName: string): string => {
  const slug = displayName
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 55)

  // The pattern requires a leading alphanumeric and at least two characters, so a name made
  // entirely of punctuation or a single letter still has to produce something valid.
  const seeded = slug.length >= 2 ? slug : `agent-${slug}`.replace(/-$/, '')
  return seeded.replace(/^-+/, '') || 'agent'
}

export const uniqueAgentId = (base: string): string => {
  const suffix = Math.random().toString(36).slice(2, 6)
  return `${base.slice(0, 57)}-${suffix}`
}
